// src/mapBuildings.js
import PopupMap from "./PopupMap.svelte";

/**
 * Gets the popup configuration for a building
 * @param {Object} building - The building object
 * @param {string} [building.title] - Building title
 * @param {string} [building.popupText] - Popup content text
 * @param {string[]} [building.sceneIds] - Array of scene IDs linked to this building
 * @param {number[]} [building.directions] - Array of direction coordinates
 * @param {Object} [building.popup] - Optional pre-configured popup object
 * @param {Object[]} scenes - Array of available scenes
 * @returns {Object|undefined} Popup configuration object or undefined if no popup
 */
function getPopup(building, scenes) {
  if (!building) return;

  // If building has pre-configured popup object, use it directly
  if (building.popup && typeof building.popup === "object") {
    return building.popup;
  }

  const { title, popupText: content, sceneIds, directions } = building;

  let popup = null;
  const hasPopup =
    (title && content && content.trim().length > 0) ||
    (title && sceneIds?.length > 0) ||
    (title && directions?.length > 0);

  if (hasPopup) {
    // Convert the scene ids into linked scenes with metadata
    const linkedscenes = sceneIds?.map((sceneId) => {
      const scene = scenes?.find((scene) => scene.id === sceneId);
      return {
        id: sceneId,
        name: scene?.name,
        title: scene?.title,
      };
    });

    popup = {
      title,
      content,
      linkedscenes,
      directions,
    };
  }
  return popup;
}

/**
 * Adds interactive 3D buildings to the Mapbox map
 * @param {Object} config - Configuration object
 * @param {Object} config.map - Mapbox map instance
 * @param {string} config.tilesetUrl - URL for the building tileset
 * @param {string} config.tilesetLayerName - Layer name in the tileset
 * @param {string} [config.labelsUrl] - URL for building labels tileset
 * @param {string} [config.labelsLayerName] - Layer name for labels
 * @param {string} config.buildingColor - Default color for buildings
 * @param {string} config.buildingHoverColor - Default hover color for buildings
 * @param {Object[]} config.buildings - Array of building configurations
 * @param {string} config.buildings[].featureId - Unique identifier for the building
 * @param {string} [config.buildings[].color] - Optional custom color for specific building
 * @param {string} [config.buildings[].hoverColor] - Optional custom hover color
 * @param {number} config.buildingHeight - Height for 3D building extrusion
 * @param {Function} config.setTooltip - Callback to update tooltip state
 * @param {Object[]} config.scenes - Array of available scenes
 */
export function addBuildings({
  map,
  tilesetUrl,
  tilesetLayerName,
  labelsUrl,
  labelsLayerName,
  buildingColor,
  buildingHoverColor,
  buildings,
  buildingHeight,
  setTooltip,
  scenes,
}) {
  // filter out hidden buildings
  buildings = buildings.filter((building) => !building.hidden);
  
  const buildingIds = buildings?.map((i) => i["featureId"]);

  // Create a mapping of custom building colors
  const buildingColors = buildings.reduce((acc, building) => {
    if (building.featureId && building.color) {
      acc[building.featureId] = building.color;
    }
    return acc;
  }, {});

  const layers = [
    {
      id: "tour-buildings-layer",
      color: [
        "case",
        // For each building, check if it has a custom color
        ["has", ["get", "@id"], ["literal", buildingColors]],
        ["get", ["get", "@id"], ["literal", buildingColors]],
        buildingColor // fallback to default color
      ],
      ids: buildingIds,
      showLabels: Boolean(labelsUrl && labelsLayerName),
    },
    process.env.NODE_ENV === "development" && {
      id: "tour-bg-buildings-layer",
      color: "#FF0000",
      ids: buildingIds,
      exclude: true,
    },
  ].filter(Boolean);

  const addLayer = (layer) => {
    const filter = layer.exclude
      ? ["!", ["in", ["get", "@id"], ["literal", layer.ids]]]
      : ["in", ["get", "@id"], ["literal", layer.ids]];

    map.addLayer({
      id: layer.id,
      type: "fill-extrusion",
      source: "base-buildings",
      "source-layer": tilesetLayerName,
      paint: {
        "fill-extrusion-color": layer.color,
        "fill-extrusion-height": buildingHeight,
      },
      filter,
    });

    if (layer.showLabels) {
      const buildingLabels = buildings.reduce((acc, cur) => {
        if (cur.featureId && cur.title) {
          acc[cur.featureId] = cur.title;
        }
        return acc;
      }, {});

      map.addLayer({
        id: layer.id + "-labels",
        type: "symbol",
        source: "labels-buildings",
        "source-layer": labelsLayerName,
        layout: {
          "text-field": ["get", ["get", "@id"], ["literal", buildingLabels]],
          "text-size": 16,
          "text-allow-overlap": false,
          "text-optional": true,
        },
        paint: {
          "text-color": "#000",
          "text-halo-color": "#fff",
          "text-halo-width": 2,
        },
        filter,
        minzoom: 15,
      });
    }
  };

  map.addSource("base-buildings", {
    type: "vector",
    url: tilesetUrl,
  });

  if (Boolean(labelsUrl && labelsLayerName)) {
    map.addSource("labels-buildings", {
      type: "vector",
      url: labelsUrl,
    });
  }

  layers.forEach(addLayer);

  map.on("click", "tour-buildings-layer", function (e) {
    if (!e.features) return;
    const id = e.features[0].properties["@id"];
    const building = buildings.find((b) => b.featureId === id);
    const popup = getPopup(building, scenes);
    if (popup) {
      // if (popup) open(PopupMap, popup, popupOptions);
      const popupMap = new PopupMap({
        target: document.body,
        props: popup,
      });
    } else {
      console.log(id);
      console.log(e.features[0].properties);
    }
  });

  let animatedBuildingId = null;

  map.on("mousemove", function (e) {
    const layerIds = layers.map((layer) => layer.id);
    const featuresUnderMouse = map.queryRenderedFeatures(e.point, {
      layers: layerIds,
    });

    if (!featuresUnderMouse.length) {
      layers.forEach((layer) =>
        map.setPaintProperty(layer.id, "fill-extrusion-color", layer.color),
      );
      animatedBuildingId = null;
      setTooltip(null);
      map.getCanvas().style.cursor = "";
      return;
    }

    const hoveredFeature = featuresUnderMouse[0];
    const hoveredFeatureId = hoveredFeature.properties["@id"];
    if (!hoveredFeatureId) return;
    // Tooltip
    const hoveredBuilding = buildings.find(
      (b) => b.featureId === hoveredFeatureId,
    );
    const tooltipText = hoveredBuilding
      ? hoveredBuilding.title
      : hoveredFeature.properties["name"];
    const tooltipX = e.point.x;
    const tooltipY = e.point.y;
    setTooltip({ text: tooltipText, x: tooltipX, y: tooltipY });

    const hoveredLayerId = hoveredFeature.layer.id;
    const layer = layers.find((layer) => layer.id === hoveredLayerId);
    
    // Get custom hover color or use default
    const hoverColorToUse = hoveredBuilding?.hoverColor || buildingHoverColor;

    // For non-hovered buildings, use their custom colors or default
    const nonHoveredExpression = [
      "case",
      ["has", ["get", "@id"], ["literal", buildingColors]],
      ["get", ["get", "@id"], ["literal", buildingColors]],
      layer.color === buildingColor ? buildingColor : layer.color
    ];

    map.setPaintProperty(hoveredLayerId, "fill-extrusion-color", [
      "match",
      ["get", "@id"],
      hoveredFeatureId,
      hoverColorToUse,
      nonHoveredExpression,
    ]);

    if (animatedBuildingId !== hoveredFeatureId) {
      animatedBuildingId = hoveredFeatureId;
      if (!animatedBuildingId) return;

      let startHeight = buildingHeight;
      let endHeight = startHeight * 3;
      let duration = 500;
      let startTime = performance.now();

      function animateHeight(timestamp) {
        let elapsedTime = timestamp - startTime;
        let progress = elapsedTime / duration;
        let currentHeight = startHeight + (endHeight - startHeight) * progress;

        if (animatedBuildingId === hoveredFeatureId) {
          map.setPaintProperty(hoveredLayerId, "fill-extrusion-height", [
            "match",
            ["get", "@id"],
            animatedBuildingId,
            currentHeight,
            startHeight,
          ]);
          if (elapsedTime < duration) {
            requestAnimationFrame(animateHeight);
          }
        } else if (animatedBuildingId === null) {
          map.setPaintProperty(hoveredLayerId, "fill-extrusion-height", [
            "match",
            ["get", "@id"],
            hoveredFeatureId,
            buildingHeight,
            buildingHeight,
          ]);
        }
      }

      requestAnimationFrame(animateHeight);
    }

    // TODO only show pointer if there is a popup
    map.getCanvas().style.cursor = "pointer";
  });
}
